import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import Annotation from "../../model/Annotation";
import { useApiClient } from "../../contexts/ApiClientContext";
import APIEndPoints from "../../utils/APIEndPoints";
import DownloadCode from "../../model/DownloadCode";
import Loading from "../common/Loading";
import ImgUtil from "../../utils/ImgUtil";
import ButtonInfo from "../common/button/ButtonInfo";
import AppIcons from "../../utils/AppIcons";
import TimeUtil from "../../utils/TimeUtil";
import AnnotationInfoType from "../../model/enums/AnnotationInfoType";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import ButtonType from "../common/button/ButtonType";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import AppSubSections from "../../utils/AppSubSections";
import EmbeddingToolInfo from "../../utils/EmbeddingToolInfo";
import { useAuth } from "../../contexts/AuthContext";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import { useAlerts } from "../../contexts/AlertsContext";
import ReportButton from "../common/ReportButton";

function AnnotationDetails() {
  // read the ID from the url
  const { id } = useParams();

  const authManager = useAuth();
  // manage loading animation
  const [loading, setLoading] = useState(true);
  // set the data
  const [data, setData] = useState<Annotation>(Annotation.EmptyInstance());
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the alerts system
  const alertsManager = useAlerts();

  // load record data from the server
  useEffect(() => {
    window.scrollTo(0, 0);

    const getAllData = async () => {
      try {
        let texture: Annotation | undefined = undefined;
        if (id) {
          texture = await apiClient?.get<Annotation>({
            url: APIEndPoints.ANNOTATIONS.ONE(parseInt(id)),
          });
          setData(texture);
        }
      } catch (error: any) {
        if (!error.processed) {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Something went wrong. Try again later!",
          });
        }
      }
      setLoading(false);
    };
    getAllData();
  }, [apiClient, id]);

  const getDownloadCode = async (id: number) => {
    const responseData = await apiClient?.get<DownloadCode>({
      url: APIEndPoints.DOWNLOADS.CODE(id),
      requiresToken: true,
    });
    console.log(responseData);
    window.location.href = APIEndPoints.DOWNLOADS.FILE(
      responseData.download_code,
    );
  };

  const handleDelete = async () => {
    // launch delete
    await apiClient?.delete<Annotation>({
      url: APIEndPoints.ANNOTATIONS.ONE(parseInt(id!)),
    });
    // redirect back to the 3D Scan
    navigate(
      SETTINGS.get_path({
        section: AppSections._3DINSTANCES,
        id: data.id_3dinstance,
        subsection: AppSubSections.VIEW,
      }),
    );
  };

  if (loading) {
    return <Loading />;
  }

  const infoImages = data.annotation_infos
    ? data.annotation_infos
        .filter((item) => item.type === AnnotationInfoType.IMAGE)
        .map((item) => {
          return (
            <a
              className="link-download block mt-2 mb-2"
              key={item.id}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                getDownloadCode(item.file!);
              }}
              download={item.annotation_info_name}
            >
              {item.annotation_info_name}
            </a>
          );
        })
    : undefined;

  console.log("infoImages", infoImages);

  const infoSounds = data.annotation_infos
    ? data.annotation_infos
        .filter((item) => item.type === AnnotationInfoType.SOUND)
        .map((item) => {
          return (
            <a
              className="link-download block mt-2 mb-2"
              key={item.id}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                getDownloadCode(item.file!);
              }}
              download={item.annotation_info_name}
            >
              {item.annotation_info_name}
            </a>
          );
        })
    : [];

    
  const canEdit =
    !EmbeddingToolInfo.isChemiTool() &&
    (data.id_user === authManager.getUserID() || authManager.isAdmin());

  return (
    <div className="details-page">
      <div className="details-header">
        {/* Image */}
        <div className="details-icon">
          <div>
            <img
              src={
                ImgUtil.display_icon(data.icon_path) ||
                process.env.REACT_APP_PUBLIC_URL +
                  "/resources/images/no-image.png"
              }
              alt="e3D icon"
            />
          </div>
        </div>

        {/* Info Section */}
        <div className="details-header-content">
          <div className="details-title">
            <h1>{data.name}</h1>
          </div>
          <div className="text-sm mt-4 w-full flex flex-col">
            <ButtonInfo>
              {AppIcons.CLOCK}
              created on: <span>{TimeUtil.display_date(data.created_on)}</span>
            </ButtonInfo>
            <ButtonInfo>
              {AppIcons.USER}
              created by: <span>{data.username}</span>
            </ButtonInfo>
            <ButtonInfo>
              {AppIcons.DETAIL_LEVEL}
              type: <span>{data.type}</span>
            </ButtonInfo>
            <ButtonInfo>
              {AppIcons.IMAGES}
              <span>
                {
                  data.annotation_infos?.filter(
                    (info) => info.type === AnnotationInfoType.IMAGE,
                  ).length
                }
              </span>{" "}
              images
            </ButtonInfo>
            <ButtonInfo>
              {AppIcons.FILE_SOUND}
              <span>
                {
                  data.annotation_infos?.filter(
                    (info) => info.type === AnnotationInfoType.SOUND,
                  ).length
                }
              </span>{" "}
              sounds
            </ButtonInfo>
          </div>
          <div>

            {canEdit && 
              <Button
                purpose={ButtonAction.DELETE}
                label="Delete annotation"
                onClick={handleDelete}
              />
            }
             <ReportButton
                entityType="Annotation"
                entityName={data.name}
                entityId={data.id || 0}
              />
          </div>
        </div>
      </div>
      {/* Details Section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>Annotation details</h2>
          {canEdit && (
            <Button
              label="Edit information"
              purpose={ButtonAction.EDIT}
              type={ButtonType.ICON_TEXT}
              onClick={() =>
                navigate(
                  SETTINGS.get_path({
                    section: AppSections.ANNOTATIONS,
                    id: data.id,
                    subsection: AppSubSections.EDIT,
                  }),
                )
              }
            />
          )}
        </div>
        <p className="details-description">{data.description}</p>

        <div className="details-section-content">
          <table className="details-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>GUID</td>
                <td>{data.GUID ?? "Not found"}</td>
              </tr>
              <tr>
                <td>Annotation type</td>
                <td>{data.type ?? "Not found"}</td>
              </tr>
              <tr>
                <td>Shapes and Context file</td>
                <td>
                  {data.shape_and_context && (
                    <a
                      className="link-download"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        getDownloadCode(data.shape_and_context!);
                      }}
                    >
                      click here to download file
                    </a>
                  )}
                  {!data.shape_and_context &&
                    "no shapes file found -> invalid record"}
                </td>
              </tr>
              <tr>
                <td>Image files</td>
                <td>{infoImages ?? <>no images attached</>}</td>
              </tr>
              <tr>
                <td>Sound files</td>
                <td>{infoSounds ?? <>no images attached</>}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AnnotationDetails;
